import {WorkforceCriteria} from './workforce-team-criteria.state';
import {TeamCreationIntent} from './workforce-team-creation.state';
export interface EditorQuery {criteria: WorkforceCriteria; search: string; }
export interface EditorAction {selected: boolean; employeeKey?: string; query?: EditorQuery; }
export interface EditorEmployee {
  employeeKey: string; rowId: string; firstName: string; lastName: string; primaryEmail: string;
  selected: boolean; currentMember: boolean; reason: string | null; [key: string]: any;
}
export interface EditorPage {
  companyId: string; snapshotId: string; teamId: string | null; membershipBaseline: string | null;
  selectionAvailable?: boolean;
  employees: EditorEmployee[]; next: string | null;
  counts: {displayed: number; selected: number; selectedDisplayed: number; selectableDisplayed: number; selectedSelectableDisplayed: number};
}
export interface EditorApi {
  getWorkforceEditorPage(companyId: string, request: any): Promise<EditorPage>;
  getWorkforceBrowseContext(companyId: string): Promise<any>;
  getWorkforceTeamCriteria(companyId: string, teamId: string): Promise<any>;
  getWorkforceTeamSettings(companyId: string, teamId: string): Promise<any>;
  getWorkforceCriteriaOptions(companyId: string, request: any): Promise<{values: string[]; next: string | null}>;
}
const all = (): WorkforceCriteria => ({version: 1, allEligible: true, rules: []});
export class WorkforceTeamEditorState {
  readonly fields = [['location', 'Location'], ['department', 'Department'], ['division', 'Division'],
    ['jobTitle', 'Payroll Job Title'], ['payGroup', 'Pay Type'], ['workerType', 'Employee Type'], ['state', 'State']];
  snapshotId = ''; membershipBaseline = ''; baseline: {name: string; amount: number} | null = null;
  revision = 0; criteria: WorkforceCriteria = all(); originalCriteria = '';
  search = ''; actions: EditorAction[] = []; rows: EditorEmployee[] = [];
  options: Record<string, string[]> = {}; optionNext: Record<string, string | null> = {};
  optionSearch: Record<string, string> = {}; optionLoading: Record<string, boolean> = {};
  counts: EditorPage['counts'] | null = null;
  notice = '';
  selectionAvailable = true;
  hasPrevious = false;
  loading = false; counting = false; error = ''; next: string | null = '0';
  private disposed = false;
  private version = 0; private countVersion = 0; private optionVersions: Record<string, number> = {};
  private searchTimer?: ReturnType<typeof setTimeout>;
  constructor(private api: EditorApi, readonly companyId: string, readonly teamId?: string, readonly directory = false) {}
  get query(): EditorQuery { return {criteria: this.criteria, search: this.search}; }
  get ready(): boolean { return !this.disposed && this.selectionAvailable && !!this.snapshotId && (!this.teamId || !!this.membershipBaseline && !!this.baseline) && !this.error; }
  get allSelected(): boolean { return !!this.counts?.selectableDisplayed && this.counts.selectedSelectableDisplayed === this.counts.selectableDisplayed; }
  get someSelected(): boolean { return !!this.counts?.selectedSelectableDisplayed && !this.allSelected; }
  get chips(): Array<{field: string; label: string; value: string}> {
    return this.criteria.rules.flatMap(rule => rule.values.map(value => ({field: rule.field,
      label: this.fields.find(field => field[0] === rule.field)?.[1] || rule.field, value})));
  }
  values(field: string): string[] { return this.criteria.rules.find(rule => rule.field === field)?.values || []; }
  private request(extra: any = {}): any {
    return {snapshotId: this.snapshotId, ...(this.directory ? {directory: true} : {}), ...(this.teamId ? {teamId: this.teamId} : {}), query: this.query,
      actions: this.actions, ...(this.membershipBaseline ? {membershipBaseline: this.membershipBaseline} : {}), ...extra};
  }
  private accept(page: EditorPage): void {
    if (page.companyId !== this.companyId || page.snapshotId !== this.snapshotId || page.teamId !== (this.teamId || null)) throw new Error('Workforce source changed. Reopen the editor.');
    this.selectionAvailable = page.selectionAvailable !== false;
    if (page.membershipBaseline) {
      if (this.membershipBaseline && this.membershipBaseline !== page.membershipBaseline) throw new Error('Team members changed. Reopen the editor.');
      this.membershipBaseline = page.membershipBaseline;
    }
  }
  async open(browseContext?: any): Promise<void> {
    if (this.disposed) return;
    const version = ++this.version; this.loading = true;
    try {
      // Directory reads share the parent's authorized source. An independent
      // context read could silently bind selection to a different snapshot.
      const context = browseContext || await this.api.getWorkforceBrowseContext(this.companyId);
      if (version !== this.version) return;
      if (context.scope?.companyId !== this.companyId || context.scope?.providerSlug !== 'ukg_ready' || context.mode !== 'snapshot' || context.availability !== 'ready' || !context.snapshot?.id) throw new Error('Workforce is unavailable. Retry when access is restored.');
      if (this.snapshotId && this.snapshotId !== context.snapshot.id) {
        this.actions = []; this.membershipBaseline = '';
        this.rows = []; this.counts = null; this.notice = 'Employee data changed. Review and select employees again. Your team details and filters are retained.';
      }
      this.snapshotId = context.snapshot.id;
      if (this.teamId) {
        const [settings, saved] = await Promise.all([this.api.getWorkforceTeamSettings(this.companyId, this.teamId), this.api.getWorkforceTeamCriteria(this.companyId, this.teamId)]);
        if (version !== this.version) return;
        if (settings.companyId !== this.companyId || settings.teamId !== this.teamId || saved.teamId !== this.teamId) throw new Error('Team scope changed. Reopen the editor.');
        if (this.baseline) {
          if (saved.revision !== this.revision) throw new Error('Team filters changed. Reopen the editor to review the current version.');
        } else {
          this.baseline = {name: settings.name, amount: settings.amount}; this.revision = saved.revision;
          this.criteria = saved.criteria || all(); this.originalCriteria = JSON.stringify(this.criteria);
        }
      }
      this.error = ''; this.loading = false;
      this.fields.forEach(([field]) => void this.loadOptions(field));
      await this.reload();
    } catch (error: any) { if (version === this.version) { this.error = error.message || 'Unable to load employees.'; this.loading = false; } }
  }
  async reload(): Promise<void> {
    if (this.disposed) return;
    clearTimeout(this.searchTimer);
    const version = ++this.version; ++this.countVersion; this.counts = null; this.counting = false;
    this.rows = []; this.hasPrevious = false; this.next = '0'; this.error = ''; this.loading = false;
    await this.more();
    if (!this.disposed && version === this.version && !this.error) void this.recount();
  }
  searchChanged(): void {
    clearTimeout(this.searchTimer); ++this.version; ++this.countVersion; this.counts = null; this.counting = false;
    this.rows = []; this.next = null; this.loading = true;
    this.searchTimer = setTimeout(() => void this.reload(), 250);
  }
  filterChanged(field: string, values: string[]): void {
    const rules = this.criteria.rules.filter(rule => rule.field !== field);
    if (values.length) rules.push({field: field as any, operator: 'in', values: [...values]});
    this.criteria = {version: 1, allEligible: !rules.length, rules}; void this.reload();
  }
  clearFilters(): void { this.criteria = all(); void this.reload(); }
  removeFilter(field: string, value: string): void { this.filterChanged(field, this.values(field).filter(item => item !== value)); }
  async loadOptions(field: string, more = false): Promise<void> {
    const version = (this.optionVersions[field] || 0) + 1; this.optionVersions[field] = version;
    this.optionLoading[field] = true;
    try {
      const page = await this.api.getWorkforceCriteriaOptions(this.companyId, {snapshotId: this.snapshotId, field, ...(this.directory ? {directory: true} : {}),
        prefix: this.optionSearch[field] || '', ...(more && this.optionNext[field] ? {after: this.optionNext[field]} : {})});
      if (this.optionVersions[field] !== version) return;
      this.options[field] = [...new Set([...this.values(field), ...(more ? this.options[field] || [] : []), ...page.values])];
      this.optionNext[field] = page.next;
      if (this.error === 'Unable to load filter values. Retry.') this.error = '';
    } catch (_) { if (this.optionVersions[field] === version) this.error = 'Unable to load filter values. Retry.'; }
    finally { if (this.optionVersions[field] === version) this.optionLoading[field] = false; }
  }
  async more(before?: string): Promise<void> {
    if (this.disposed || this.loading || !this.snapshotId || !before && this.next === null) return;
    const version = this.version; this.loading = true;
    let cursor = before || this.next || '0';
    let visible = 0;
    try {
      // Seek through sparse source pages without retaining the unfiltered census.
      for (;;) {
        const actions = this.actions;
        const page = await this.api.getWorkforceEditorPage(this.companyId, this.request(before ? {before: cursor} : {after: cursor}));
        if (version !== this.version) return;
        // Repeat this page if checkboxes changed while it was loading. The server
        // remains authoritative for selection and protected membership rules.
        if (actions !== this.actions) continue;
        this.accept(page);
        if (before) { this.hasPrevious = !!page.next; this.rows = [...page.employees, ...this.rows].slice(0, 200); this.next = this.rows[this.rows.length - 1]?.rowId || null; }
        else { if (this.rows.length + page.employees.length > 200) this.hasPrevious = true; this.rows = [...this.rows, ...page.employees].slice(-200); this.next = page.next; }
        // A single sparse match may not overflow the list, so no scroll event
        // could request its successors. Fill a bounded visible batch instead.
        visible += page.employees.length;
        if (visible >= 50 || !page.next) break;
        cursor = page.next;
      }
    } catch (error: any) { if (version === this.version) this.error = error.message || 'Unable to load employees. Retry.'; }
    finally { if (version === this.version) this.loading = false; }
  }
  async recount(): Promise<void> {
    if (this.disposed) return;
    const version = ++this.countVersion, view = this.version;
    this.counting = true; this.counts = null;
    const counts = {displayed: 0, selected: 0, selectedDisplayed: 0, selectableDisplayed: 0, selectedSelectableDisplayed: 0};
    try {
      let after: string | null = '0';
      do {
        const page = await this.api.getWorkforceEditorPage(this.companyId, this.request({after, summary: true}));
        if (version !== this.countVersion || view !== this.version) return;
        this.accept(page);
        (Object.keys(counts) as Array<keyof typeof counts>).forEach(key => counts[key] += page.counts[key]);
        after = page.next;
      } while (after);
      this.counts = counts;
    } catch (error: any) { if (version === this.countVersion && view === this.version) this.error = error.message || 'Unable to count the complete selection. Retry.'; }
    finally { if (version === this.countVersion && view === this.version) this.counting = false; }
  }
  toggle(row: EditorEmployee, selected: boolean): void {
    if (this.disposed || !this.selectionAvailable || row.reason) return;
    this.addAction({employeeKey: row.employeeKey, selected});
    if (!this.error) row.selected = selected;
  }
  toggleAll(selected: boolean): void {
    if (this.disposed || !this.selectionAvailable) return;
    this.addAction({query: JSON.parse(JSON.stringify(this.query)), selected});
    if (!this.error) this.rows.forEach(row => { if (!row.reason) row.selected = selected; });
  }
  private addAction(action: EditorAction): void {
    const actions = [...this.actions, action];
    if (new TextEncoder().encode(JSON.stringify(this.request({actions}))).length > 60000) { this.error = 'This selection has too many individual changes. Simplify the filters before saving.'; return; }
    this.actions = actions; void this.recount();
  }
  clearSelection(): void {
    if (this.disposed) return;
    this.actions = []; ++this.version; ++this.countVersion; clearTimeout(this.searchTimer);
    this.loading = false; this.counting = false;
    this.rows.forEach(row => { row.selected = false; });
    if (this.counts) this.counts = {...this.counts, selected: 0, selectedDisplayed: 0, selectedSelectableDisplayed: 0};
  }
  intent(name: string, amount: number): TeamCreationIntent {
    return {format: 'entries_v1', snapshotId: this.snapshotId, actions: JSON.parse(JSON.stringify(this.actions)),
      ...(this.teamId ? {editMembers: true, teamId: this.teamId, membershipBaseline: this.membershipBaseline,
        settingsUpdate: {settings: {name, amount}, baseline: this.baseline!},
        ...(JSON.stringify(this.criteria) !== this.originalCriteria ? {criteriaUpdate: {revision: this.revision, criteria: this.criteria}} : {})}
        : {teamName: name, amount, criteria: this.criteria})};
  }
  destroy(): void { this.disposed = true; this.rows = []; this.actions = []; this.options = {}; this.counts = null; ++this.version; ++this.countVersion; clearTimeout(this.searchTimer); Object.keys(this.optionVersions).forEach(key => this.optionVersions[key]++); }
}
